import { funnelDefinitionEach } from '@rightmessage/flow-builder'
import { definitionLogicItems } from '@/modules/segment/services/segmentUtilities'
import type { Dimension, Flow, LogicDefinition } from '@/types/config'

export const MIN_EXECUTIONS_FOR_IDENTIFICATION_WARNING = 25
export const MAX_HEALTHY_IDENTIFICATION_RATE = 20

export type DimensionSyncStatus =
  | 'all_values'
  | 'some_values'
  | 'no_values'
  | 'no_field'
  | 'no_integration'
  | 'no_segment_group'
  | null
  | undefined

export type FlowIdentificationQuestion = {
  id: string
  dimensionId?: string | null
  syncToField?: string
}

export type FlowIdentificationAssessment = {
  totalQuestionCount: number
  syncedQuestionCount: number
  hasExistingIdentitySignal: boolean
  isIdentityDependentSurvey: boolean
  hasSuspiciouslyLowIdentification: boolean
}

type LogicItemWithType = {
  $type?: string
}

const questionIsSynced = (
  question: FlowIdentificationQuestion,
  dimensionSyncStatusById: Record<string, DimensionSyncStatus>
) => {
  if (question.dimensionId) {
    const syncStatus = dimensionSyncStatusById[question.dimensionId]
    return syncStatus === 'all_values' || syncStatus === 'some_values'
  }

  return Boolean(question.syncToField)
}

const logicItemHasExistingIdentitySignal = (logicItem: unknown) => {
  if (!logicItem || typeof logicItem !== 'object') return false

  const { $type } = logicItem as LogicItemWithType
  return $type === 'isSubscriber'
}

export const dimensionHasSyncConfiguration = (
  dimension: Pick<Dimension, 'syncMode' | 'syncMappings' | 'syncToCustomField'> | null | undefined
) => {
  if (!dimension) return false

  const syncMode = dimension.syncMode || 'field'

  if (syncMode === 'tag') {
    return Object.keys(dimension.syncMappings ?? {}).length > 0
  }

  return Boolean(dimension.syncToCustomField)
}

export const flowHasExistingIdentitySignal = (flow: Flow | null | undefined) => {
  if (!flow?.funnelDefinition) return false

  let hasExistingIdentitySignal = false

  funnelDefinitionEach(flow.funnelDefinition, (node) => {
    if (hasExistingIdentitySignal || node.type !== 'yesno') return
    if (!node.condition || typeof node.condition !== 'object') return

    const logicItems = definitionLogicItems(node.condition as LogicDefinition)
    hasExistingIdentitySignal = logicItems.some(logicItemHasExistingIdentitySignal)
  })

  return hasExistingIdentitySignal
}

export const assessFlowIdentificationHealth = ({
  flow,
  hasExistingIdentitySignal,
  questions,
  dimensionSyncStatusById,
  totalExecutions,
  identifiedRate
}: {
  flow: Flow | null | undefined
  hasExistingIdentitySignal: boolean
  questions: FlowIdentificationQuestion[]
  dimensionSyncStatusById: Record<string, DimensionSyncStatus>
  totalExecutions: number
  identifiedRate: number
}): FlowIdentificationAssessment => {
  const totalQuestionCount = questions.length
  const syncedQuestionCount = questions.filter((question) =>
    questionIsSynced(question, dimensionSyncStatusById)
  ).length

  const isIdentityDependentSurvey = Boolean(
    flow &&
      !flow.isSystemFlow &&
      hasExistingIdentitySignal &&
      totalQuestionCount > 0 &&
      syncedQuestionCount > 0
  )

  const hasSuspiciouslyLowIdentification = Boolean(
    isIdentityDependentSurvey &&
      totalExecutions >= MIN_EXECUTIONS_FOR_IDENTIFICATION_WARNING &&
      identifiedRate < MAX_HEALTHY_IDENTIFICATION_RATE
  )

  return {
    totalQuestionCount,
    syncedQuestionCount,
    hasExistingIdentitySignal,
    isIdentityDependentSurvey,
    hasSuspiciouslyLowIdentification
  }
}
